// src/modules/dl/EvalLab.jsx
// LLM Evaluation Lab — Perplexity · BLEU/ROUGE · Benchmarks · Model sizing · Bias & Safety

import React, { useState } from 'react';
import {
  LineChart, Line, BarChart, Bar, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell, ReferenceLine,
} from 'recharts';
import { Panel, Metric, LogPanel, timestamp } from '../../components/ui.jsx';

const AC = 'var(--red)';
import { _livePalette as P } from '../../context/paletteSync';

const Btn = ({onClick,children,color=AC,disabled,style}) => (
  <button onClick={onClick} disabled={disabled} style={{padding:'8px 16px',background:color+'22',border:`1px solid ${color}`,color,fontSize:'10px',cursor:disabled?'not-allowed':'pointer',borderRadius:'3px',fontFamily:'JetBrains Mono',fontWeight:700,letterSpacing:'.08em',opacity:disabled?.4:1,...style}}>{children}</button>
);
const Sl = ({label,value,min,max,step=1,onChange,unit='',color=AC}) => (
  <div style={{marginBottom:'10px'}}>
    <div style={{display:'flex',justifyContent:'space-between',marginBottom:'3px'}}>
      <span style={{fontSize:'10px',color:'var(--t2)'}}>{label}</span>
      <span style={{fontSize:'10px',color,fontWeight:700}}>{typeof value==='number'?value.toFixed(step<0.01?4:step<1?2:0):value}{unit}</span>
    </div>
    <div style={{position:'relative',height:'3px',background:'var(--b0)',borderRadius:'2px'}}>
      <div style={{position:'absolute',left:0,height:'100%',background:`linear-gradient(90deg,${color}44,${color})`,width:`${((value-min)/(max-min))*100}%`}}/>
      <input type="range" min={min} max={max} step={step} value={value} onChange={e=>onChange(parseFloat(e.target.value))}
        style={{position:'absolute',inset:'-7px 0',width:'100%',opacity:0,cursor:'pointer',height:'18px'}}/>
    </div>
  </div>
);

// ─── MATH ─────────────────────────────────────────────────────────────────────
const randn = () => { let u=0,v=0; while(!u)u=Math.random(); while(!v)v=Math.random(); return Math.sqrt(-2*Math.log(u))*Math.cos(2*Math.PI*v); };

// BLEU score (simplified unigram/bigram)
const ngramOverlap = (hyp, ref, n) => {
  const hypNg=[], refNg=[];
  for(let i=0;i<=hyp.length-n;i++) hypNg.push(hyp.slice(i,i+n).join(' '));
  for(let i=0;i<=ref.length-n;i++) refNg.push(ref.slice(i,i+n).join(' '));
  const matches=hypNg.filter(ng=>refNg.includes(ng)).length;
  return hypNg.length>0?matches/hypNg.length:0;
};

const bleuScore = (hypothesis, reference) => {
  const hyp=hypothesis.toLowerCase().split(/\s+/);
  const ref=reference.toLowerCase().split(/\s+/);
  const p1=ngramOverlap(hyp,ref,1);
  const p2=ngramOverlap(hyp,ref,2);
  const bp=hyp.length>=ref.length?1:Math.exp(1-ref.length/hyp.length);
  return +(bp*Math.sqrt(p1*p2)*100).toFixed(2);
};

// ROUGE-1 F1
const rouge1 = (hyp, ref) => {
  const h=new Set(hyp.toLowerCase().split(/\s+/));
  const r=ref.toLowerCase().split(/\s+/);
  const overlap=r.filter(w=>h.has(w)).length;
  const precision=overlap/h.size||0;
  const recall=overlap/r.length||0;
  const f1=precision+recall>0?2*precision*recall/(precision+recall):0;
  return +(f1*100).toFixed(2);
};

// Perplexity = exp(avg negative log likelihood)
const perplexity = (probs) => {
  const avgNLL=-probs.reduce((s,p)=>s+Math.log(Math.max(1e-10,p)),0)/probs.length;
  return +Math.exp(avgNLL).toFixed(2);
};

const TABS=['perplexity','bleu-rouge','benchmarks','sizing-laws','leaderboard'];

// Benchmark data for major models
const BENCHMARK_DATA = [
  {model:'GPT-4o',    mmlu:88.7,hellaswag:95.3,gsm8k:92.0,humaneval:90.2,arc:96.7,color:'#fbbf24'},
  {model:'Claude 3.5',mmlu:88.3,hellaswag:95.4,gsm8k:93.7,humaneval:92.0,arc:96.4,color:'#a78bfa'},
  {model:'Gemini 1.5',mmlu:83.7,hellaswag:92.0,gsm8k:90.0,humaneval:84.1,arc:94.4,color:'#22d3ee'},
  {model:'Llama 3 70B',mmlu:82.0,hellaswag:93.0,gsm8k:90.1,humaneval:81.7,arc:93.9,color:'#4ade80'},
  {model:'Mistral 7B', mmlu:64.1,hellaswag:81.3,gsm8k:52.2,humaneval:30.5,arc:72.0,color:'#fb923c'},
  {model:'GPT-3.5',   mmlu:70.0,hellaswag:85.5,gsm8k:57.1,humaneval:73.5,arc:85.2,color:'#60a5fa'},
];

export default function EvalLab() {
  const [tab, setTab] = useState('perplexity');
  const [logs, setLogs] = useState([{ts:'00:00:00',msg:'Eval Lab — measure and compare language model performance.',t:'info'}]);
  const log = (msg,t='info') => setLogs(l=>[...l.slice(-80),{ts:timestamp(),msg,t}]);

  // Perplexity
  const [pplText, setPplText] = useState('The cat sat on the mat');
  const [modelQuality, setModelQuality] = useState(0.5);
  const [pplResult, setPplResult] = useState(null);

  // BLEU/ROUGE
  const [hypothesis, setHypothesis] = useState('The cat is on the mat');
  const [reference, setReference] = useState('The cat sat on the mat');
  const [bleuResult, setBleuResult] = useState(null);

  // Benchmarks
  const [selectedModels, setSelectedModels] = useState(['GPT-4o','Claude 3.5','Llama 3 70B']);
  const [benchResult, setBenchResult] = useState(null);

  // Sizing laws
  const [sizingResult, setSizingResult] = useState(null);

  // Leaderboard
  const [lbResult, setLbResult] = useState(null);

  const runPerplexity = () => {
    const words = pplText.split(/\s+/).filter(Boolean);
    // Simulate token probabilities: good model = higher p per token
    const probs = words.map((_,i)=>Math.max(0.01,Math.min(0.99,modelQuality*(0.5+Math.sin(i)*0.3)+randn()*0.1)));
    const ppl = perplexity(probs);
    const tokenPpts = probs.map((p,i)=>({token:words[i],prob:+p.toFixed(4),contrib:+Math.exp(-Math.log(p)).toFixed(4)}));
    setPplResult({ppl, tokenPpts, avgProb:+(probs.reduce((s,p)=>s+p,0)/probs.length).toFixed(4)});
    log(`Perplexity = ${ppl} | avg token prob = ${(probs.reduce((s,p)=>s+p,0)/probs.length).toFixed(3)} | words=${words.length}`,'ok');
  };

  const runBleu = () => {
    const bleu = bleuScore(hypothesis, reference);
    const rouge = rouge1(hypothesis, reference);
    // Visualize word overlap
    const hypWords = hypothesis.toLowerCase().split(/\s+/);
    const refWords = new Set(reference.toLowerCase().split(/\s+/));
    const wordViz = hypWords.map(w=>({word:w, match:refWords.has(w)}));
    setBleuResult({bleu, rouge, wordViz});
    log(`BLEU=${bleu} ROUGE-1=${rouge} | "${hypothesis}" vs "${reference}"`,'ok');
  };

  const runBenchmarks = () => {
    const filtered = BENCHMARK_DATA.filter(m=>selectedModels.includes(m.model));
    const radar = ['mmlu','hellaswag','gsm8k','humaneval','arc'].map(bench=>({
      bench: bench.toUpperCase(),
      ...Object.fromEntries(filtered.map(m=>[m.model, m[bench]]))
    }));
    setBenchResult({filtered, radar});
    log(`Benchmarks: ${filtered.length} models | ${['MMLU','HellaSWAG','GSM8k','HumanEval','ARC'].join(', ')}`,'ok');
  };

  const runSizing = () => {
    // Kaplan scaling: L ≈ (N_c/N)^0.076 + ... simplified loss vs params
    const params=[125e6,350e6,1.3e9,6.7e9,13e9,30e9,70e9,175e9];
    const result=params.map(n=>{
      const loss=+(3.1*Math.pow(1e9/n,0.076)+randn()*0.02).toFixed(4);
      const mmlu=+(30+40*(1-Math.pow(1e9/n,0.1))+randn()*1).toFixed(1);
      const ppl=+(5+20*Math.pow(1e9/n,0.15)+randn()*0.3).toFixed(2);
      return {name:n>=1e9?`${n/1e9}B`:`${n/1e6}M`,params:n,loss,mmlu,ppl};
    });
    setSizingResult(result);
    log(`Neural scaling: loss vs N for 8 model sizes (Kaplan/Chinchilla)`,'ok');
  };

  const runLeaderboard = () => {
    const all=BENCHMARK_DATA.map(m=>{
      const avg=(m.mmlu+m.hellaswag+m.gsm8k+m.humaneval+m.arc)/5;
      const cost=m.model==='GPT-4o'?15:m.model.includes('Claude')?15:m.model.includes('Gemini')?3.5:m.model.includes('Llama')?0:m.model.includes('Mistral')?0.2:0.5;
      return {...m,avg:+avg.toFixed(1),cost};
    }).sort((a,b)=>b.avg-a.avg);
    setLbResult(all);
    log(`Leaderboard computed: ${all.length} models ranked by avg benchmark score`,'ok');
  };

  return (
    <div className="anim-fadeup" style={{display:'flex',flexDirection:'column',gap:'12px'}}>
      <div style={{display:'flex',gap:'3px',background:'var(--deep)',padding:'4px',borderRadius:'3px',border:'1px solid var(--b0)',overflowX:'auto'}}>
        {TABS.map(t=><button key={t} onClick={()=>setTab(t)} style={{padding:'6px 14px',border:`1px solid ${tab===t?AC:'transparent'}`,background:tab===t?AC+'18':'transparent',color:tab===t?AC:'var(--t3)',fontSize:'10px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono',whiteSpace:'nowrap',fontWeight:tab===t?700:400}}>{t.toUpperCase()}</button>)}
      </div>

      {/* ── PERPLEXITY ── */}
      {tab==='perplexity'&&(
        <div style={{display:'grid',gridTemplateColumns:'240px 1fr',gap:'12px'}}>
          <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
            <Panel title="PPL CONFIG" accent={AC}>
              <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'4px',letterSpacing:'.1em'}}>INPUT TEXT</div>
              <input value={pplText} onChange={e=>setPplText(e.target.value)}
                style={{width:'100%',background:'var(--deep)',border:'1px solid var(--b1)',color:'var(--t1)',padding:'8px',fontSize:'11px',fontFamily:'JetBrains Mono',borderRadius:'3px',outline:'none',marginBottom:'10px',boxSizing:'border-box'}}/>
              <Sl label="Model Quality" value={modelQuality} min={0.1} max={0.9} step={0.05} onChange={setModelQuality} color={AC}
                unit={modelQuality>0.6?' (good)':modelQuality<0.3?' (bad)':''}/>
              <Btn onClick={runPerplexity} color={AC} style={{width:'100%'}}>COMPUTE PPL →</Btn>
              <div style={{marginTop:'8px',fontSize:'9px',color:'var(--t3)',lineHeight:1.7}}>
                PPL = exp(avg -log p(token)). Low PPL = model is confident and correct. High PPL = model is surprised.
              </div>
            </Panel>
            {pplResult&&(
              <Panel title="PERPLEXITY" accent={AC}>
                <div style={{textAlign:'center',padding:'12px'}}>
                  <div style={{fontFamily:'Syne',fontWeight:800,fontSize:'40px',color:pplResult.ppl<10?'#4ade80':pplResult.ppl<50?'#fbbf24':'#f87171'}}>{pplResult.ppl}</div>
                  <div style={{fontSize:'10px',color:'var(--t3)',marginTop:'4px'}}>
                    {pplResult.ppl<5?'🟢 Excellent — model very confident':pplResult.ppl<20?'🟡 Good — typical for quality LLM':pplResult.ppl<100?'🟠 Mediocre — model uncertain':'🔴 Bad — model confused'}
                  </div>
                </div>
                <Metric label="Avg Token Prob" value={pplResult.avgProb} color="#22d3ee"/>
              </Panel>
            )}
            <LogPanel logs={logs} height={90}/>
          </div>

          <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
            {pplResult
              ? <>
                  <Panel title="TOKEN-LEVEL CONTRIBUTION TO PERPLEXITY" accent={AC}>
                    <div style={{display:'flex',flexWrap:'wrap',gap:'6px',marginBottom:'12px'}}>
                      {pplResult.tokenPpts.map((t,i)=>(
                        <div key={i} style={{padding:'6px 10px',borderRadius:'4px',textAlign:'center',background:t.prob>0.6?'#4ade8018':t.prob>0.3?'#fbbf2418':'#f8717118',border:`1px solid ${t.prob>0.6?'#4ade8044':t.prob>0.3?'#fbbf2444':'#f8717144'}`}}>
                          <div style={{fontSize:'13px',color:t.prob>0.6?'#4ade80':t.prob>0.3?'#fbbf24':'#f87171',fontFamily:'JetBrains Mono',fontWeight:700}}>{t.token}</div>
                          <div style={{fontSize:'8px',color:'var(--t3)'}}>{(t.prob*100).toFixed(0)}%</div>
                        </div>
                      ))}
                    </div>
                    <ResponsiveContainer width="100%" height={140}>
                      <BarChart data={pplResult.tokenPpts}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                        <XAxis dataKey="token" tick={{fontSize:10,fill:'var(--t3)'}}/>
                        <YAxis tick={{fontSize:9,fill:'var(--t3)'}} domain={[0,1]}/>
                        <Tooltip/>
                        <ReferenceLine y={0.5} stroke="#64748b" strokeDasharray="3 2"/>
                        <Bar dataKey="prob" name="P(token)" radius={[3,3,0,0]}>
                          {pplResult.tokenPpts.map((t,i)=><Cell key={i} fill={t.prob>0.6?'#4ade80':t.prob>0.3?'#fbbf24':'#f87171'} opacity={0.85}/>)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </Panel>

                  <Panel title="PERPLEXITY CONTEXT FOR COMMON MODELS" accent="#fbbf24">
                    <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'8px'}}>
                      {[['GPT-2 (117M)','~35','#64748b'],['GPT-3 (175B)','~20','#60a5fa'],['GPT-4','~8','#fbbf24'],['Human','~7','#4ade80']].map(([m,p,c])=>(
                        <div key={m} style={{padding:'8px',background:'var(--deep)',borderRadius:'3px',textAlign:'center',borderTop:`2px solid ${c}`}}>
                          <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'3px'}}>{m}</div>
                          <div style={{fontSize:'18px',color:c,fontFamily:'Syne',fontWeight:800}}>~{p}</div>
                        </div>
                      ))}
                    </div>
                  </Panel>
                </>
              : <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'300px',color:'var(--t3)',fontSize:'12px',border:'1px dashed var(--b1)',borderRadius:'4px'}}>← Compute perplexity</div>
            }
          </div>
        </div>
      )}

      {/* ── BLEU/ROUGE ── */}
      {tab==='bleu-rouge'&&(
        <div style={{display:'grid',gridTemplateColumns:'260px 1fr',gap:'12px'}}>
          <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
            <Panel title="TEXT INPUTS" accent="#22d3ee">
              <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'3px'}}>HYPOTHESIS (model output)</div>
              <input value={hypothesis} onChange={e=>setHypothesis(e.target.value)}
                style={{width:'100%',background:'var(--deep)',border:'1px solid #22d3ee44',color:'var(--t1)',padding:'8px',fontSize:'11px',fontFamily:'JetBrains Mono',borderRadius:'3px',outline:'none',marginBottom:'8px',boxSizing:'border-box'}}/>
              <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'3px'}}>REFERENCE (ground truth)</div>
              <input value={reference} onChange={e=>setReference(e.target.value)}
                style={{width:'100%',background:'var(--deep)',border:'1px solid #4ade8044',color:'var(--t1)',padding:'8px',fontSize:'11px',fontFamily:'JetBrains Mono',borderRadius:'3px',outline:'none',marginBottom:'10px',boxSizing:'border-box'}}/>
              <Btn onClick={runBleu} color="#22d3ee" style={{width:'100%'}}>COMPUTE METRICS →</Btn>
            </Panel>
            {bleuResult&&(
              <Panel title="SCORES" accent="#22d3ee">
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'8px'}}>
                  <div style={{padding:'12px',background:'var(--deep)',borderRadius:'4px',textAlign:'center'}}>
                    <div style={{fontSize:'8px',color:'var(--t3)',marginBottom:'4px'}}>BLEU</div>
                    <div style={{fontFamily:'Syne',fontWeight:800,fontSize:'28px',color:bleuResult.bleu>60?'#4ade80':bleuResult.bleu>30?'#fbbf24':'#f87171'}}>{bleuResult.bleu}</div>
                  </div>
                  <div style={{padding:'12px',background:'var(--deep)',borderRadius:'4px',textAlign:'center'}}>
                    <div style={{fontSize:'8px',color:'var(--t3)',marginBottom:'4px'}}>ROUGE-1</div>
                    <div style={{fontFamily:'Syne',fontWeight:800,fontSize:'28px',color:bleuResult.rouge>60?'#4ade80':bleuResult.rouge>30?'#fbbf24':'#f87171'}}>{bleuResult.rouge}</div>
                  </div>
                </div>
              </Panel>
            )}
            <LogPanel logs={logs} height={100}/>
          </div>

          <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
            {bleuResult
              ? <>
                  <Panel title="WORD OVERLAP VISUALIZATION" accent="#22d3ee">
                    <div style={{marginBottom:'6px',fontSize:'10px',color:'var(--t3)'}}>Hypothesis words — green = match with reference, red = no match</div>
                    <div style={{display:'flex',flexWrap:'wrap',gap:'5px',marginBottom:'12px'}}>
                      {bleuResult.wordViz.map((w,i)=>(
                        <span key={i} style={{padding:'4px 10px',borderRadius:'3px',fontFamily:'JetBrains Mono',fontSize:'12px',fontWeight:700,background:w.match?'#4ade8018':'#f8717118',color:w.match?'#4ade80':'#f87171',border:`1px solid ${w.match?'#4ade8044':'#f8717144'}`}}>{w.word}</span>
                      ))}
                    </div>
                    <div style={{marginBottom:'6px',fontSize:'10px',color:'var(--t3)'}}>Reference:</div>
                    <div style={{display:'flex',flexWrap:'wrap',gap:'5px'}}>
                      {reference.split(/\s+/).map((w,i)=>(
                        <span key={i} style={{padding:'4px 10px',borderRadius:'3px',fontFamily:'JetBrains Mono',fontSize:'12px',background:'#22d3ee18',color:'#22d3ee',border:'1px solid #22d3ee44'}}>{w}</span>
                      ))}
                    </div>
                  </Panel>

                  <Panel title="METRIC INTERPRETATION" accent="#fbbf24">
                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'8px'}}>
                      {[
                        {metric:'BLEU',range:'0-100',good:'>30',perfect:'>60',note:'Precision-based. Misses recall. Good for MT.'},
                        {metric:'ROUGE-1',range:'0-100',good:'>40',perfect:'>70',note:'Recall-based. Good for summarization.'},
                        {metric:'ROUGE-L',range:'0-100',good:'>35',perfect:'>60',note:'Longest common subsequence.'},
                        {metric:'METEOR',range:'0-1',good:'>0.4',perfect:'>0.7',note:'Accounts for synonyms. Better than BLEU.'},
                      ].map(m=>(
                        <div key={m.metric} style={{padding:'8px',background:'var(--deep)',borderRadius:'3px'}}>
                          <div style={{fontSize:'10px',color:'#fbbf24',fontWeight:700,marginBottom:'3px'}}>{m.metric}</div>
                          <div style={{fontSize:'9px',color:'var(--t3)',lineHeight:1.6}}>Good: {m.good} | Perfect: {m.perfect}</div>
                          <div style={{fontSize:'9px',color:'var(--t2)',marginTop:'2px'}}>{m.note}</div>
                        </div>
                      ))}
                    </div>
                  </Panel>
                </>
              : <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'300px',color:'var(--t3)',fontSize:'12px',border:'1px dashed var(--b1)',borderRadius:'4px'}}>← Enter hypothesis + reference and compute</div>
            }
          </div>
        </div>
      )}

      {/* ── BENCHMARKS ── */}
      {tab==='benchmarks'&&(
        <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
          <div style={{display:'flex',gap:'8px',alignItems:'center',flexWrap:'wrap',padding:'10px',background:'var(--surface)',border:'1px solid var(--b0)',borderRadius:'4px'}}>
            <span style={{fontSize:'9px',color:'var(--t3)',letterSpacing:'.1em'}}>SELECT MODELS:</span>
            {BENCHMARK_DATA.map(m=>(
              <button key={m.model} onClick={()=>setSelectedModels(s=>s.includes(m.model)?s.filter(x=>x!==m.model):[...s,m.model])}
                style={{padding:'5px 12px',border:`1px solid ${selectedModels.includes(m.model)?m.color:'var(--b0)'}`,background:selectedModels.includes(m.model)?m.color+'18':'transparent',color:selectedModels.includes(m.model)?m.color:'var(--t3)',fontSize:'9px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono'}}>
                {m.model}
              </button>
            ))}
            <Btn onClick={runBenchmarks} color={AC} style={{marginLeft:'auto'}}>RUN →</Btn>
          </div>

          {benchResult
            ? <>
                <Panel title="BENCHMARK RADAR — MMLU · HELLASWAG · GSM8K · HUMANEVAL · ARC" accent={AC}>
                  <ResponsiveContainer width="100%" height={320}>
                    <RadarChart data={benchResult.radar}>
                      <PolarGrid stroke="#1e293b"/>
                      <PolarAngleAxis dataKey="bench" tick={{fontSize:10,fill:'var(--t3)',fontFamily:'JetBrains Mono'}}/>
                      <PolarRadiusAxis angle={30} domain={[0,100]} tick={{fontSize:8,fill:'#64748b'}}/>
                      {benchResult.filtered.map((m,i)=>(
                        <Radar key={m.model} name={m.model} dataKey={m.model} stroke={m.color} fill={m.color} fillOpacity={0.1} strokeWidth={2}/>
                      ))}
                      <Legend wrapperStyle={{fontSize:10}}/>
                    </RadarChart>
                  </ResponsiveContainer>
                </Panel>

                <Panel title="BAR COMPARISON" accent="#fbbf24">
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={benchResult.filtered.map(m=>({model:m.model,...Object.fromEntries(['mmlu','hellaswag','gsm8k','humaneval','arc'].map(b=>[b,m[b]]))}))}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                      <XAxis dataKey="model" tick={{fontSize:9,fill:'var(--t3)'}}/>
                      <YAxis tick={{fontSize:9,fill:'var(--t3)'}} domain={[0,100]}/>
                      <Tooltip/>
                      <Legend iconSize={8} wrapperStyle={{fontSize:9}}/>
                      {['mmlu','hellaswag','gsm8k','humaneval','arc'].map((b,i)=><Bar key={b} dataKey={b} fill={P[i]} opacity={0.85} radius={[2,2,0,0]}/>)}
                    </BarChart>
                  </ResponsiveContainer>
                </Panel>
              </>
            : <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'300px',color:'var(--t3)',fontSize:'12px',border:'1px dashed var(--b1)',borderRadius:'4px'}}>← Select models and run benchmarks</div>
          }
        </div>
      )}

      {/* ── SIZING LAWS ── */}
      {tab==='sizing-laws'&&(
        <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
          <Btn onClick={runSizing} color="#a78bfa" style={{alignSelf:'flex-start'}}>COMPUTE SCALING →</Btn>
          {sizingResult
            ? <>
                <Panel title="LOSS vs MODEL SIZE (KAPLAN NEURAL SCALING LAWS)" accent="#a78bfa">
                  <div style={{fontSize:'10px',color:'var(--t3)',marginBottom:'8px'}}>Each 10× increase in parameters → ~10% loss reduction. Diminishing returns follow a power law.</div>
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={sizingResult}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                      <XAxis dataKey="name" tick={{fontSize:10,fill:'var(--t3)'}}/>
                      <YAxis tick={{fontSize:9,fill:'var(--t3)'}}/>
                      <Tooltip/>
                      <Legend/>
                      <Line type="monotone" dataKey="loss" stroke="#a78bfa" strokeWidth={2.5} dot={{r:5,fill:'#a78bfa'}} name="Test Loss"/>
                    </LineChart>
                  </ResponsiveContainer>
                </Panel>

                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'10px'}}>
                  <Panel title="MMLU ACCURACY vs SIZE" accent="#22d3ee">
                    <ResponsiveContainer width="100%" height={160}>
                      <LineChart data={sizingResult}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                        <XAxis dataKey="name" tick={{fontSize:10,fill:'var(--t3)'}}/>
                        <YAxis tick={{fontSize:9,fill:'var(--t3)'}} domain={[0,100]}/>
                        <Tooltip/>
                        <Line type="monotone" dataKey="mmlu" stroke="#22d3ee" strokeWidth={2} dot={{r:4}} name="MMLU %"/>
                      </LineChart>
                    </ResponsiveContainer>
                  </Panel>
                  <Panel title="PERPLEXITY vs SIZE" accent="#fbbf24">
                    <ResponsiveContainer width="100%" height={160}>
                      <LineChart data={sizingResult}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                        <XAxis dataKey="name" tick={{fontSize:10,fill:'var(--t3)'}}/>
                        <YAxis tick={{fontSize:9,fill:'var(--t3)'}}/>
                        <Tooltip/>
                        <Line type="monotone" dataKey="ppl" stroke="#fbbf24" strokeWidth={2} dot={{r:4}} name="Perplexity"/>
                      </LineChart>
                    </ResponsiveContainer>
                  </Panel>
                </div>
              </>
            : <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'300px',color:'var(--t3)',fontSize:'12px',border:'1px dashed var(--b1)',borderRadius:'4px'}}>← Compute scaling laws</div>
          }
        </div>
      )}

      {/* ── LEADERBOARD ── */}
      {tab==='leaderboard'&&(
        <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
          <Btn onClick={runLeaderboard} color={AC} style={{alignSelf:'flex-start'}}>BUILD LEADERBOARD →</Btn>
          {lbResult
            ? <>
                <Panel title="MODEL LEADERBOARD — RANKED BY AVG BENCHMARK SCORE" accent={AC}>
                  <table style={{width:'100%',borderCollapse:'collapse',fontSize:'11px'}}>
                    <thead><tr style={{background:'var(--deep)'}}>
                      {['Rank','Model','MMLU','HellaSWAG','GSM8K','HumanEval','ARC','Avg','$/1M tokens'].map(h=><th key={h} style={{padding:'8px 10px',textAlign:'left',color:'var(--t3)',fontSize:'9px',borderBottom:'1px solid var(--b0)'}}>{h}</th>)}
                    </tr></thead>
                    <tbody>
                      {lbResult.map((m,i)=>(
                        <tr key={m.model} style={{borderBottom:'1px solid var(--b0)',background:i===0?m.color+'08':'transparent'}}>
                          <td style={{padding:'8px 10px',color:P[i%P.length],fontFamily:'Syne',fontWeight:800,fontSize:'18px'}}>{i+1}</td>
                          <td style={{padding:'8px 10px',color:m.color,fontWeight:700}}>{m.model}</td>
                          {['mmlu','hellaswag','gsm8k','humaneval','arc'].map(b=>(
                            <td key={b} style={{padding:'8px 10px',color:m[b]>85?'#4ade80':m[b]>70?'#fbbf24':'#f87171',fontFamily:'JetBrains Mono'}}>{m[b]}</td>
                          ))}
                          <td style={{padding:'8px 10px',color:m.color,fontFamily:'JetBrains Mono',fontWeight:700}}>{m.avg}</td>
                          <td style={{padding:'8px 10px',color:m.cost===0?'#4ade80':m.cost>10?'#f87171':'#fbbf24',fontFamily:'JetBrains Mono'}}>{m.cost===0?'Open':'$'+m.cost}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Panel>
              </>
            : <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'300px',color:'var(--t3)',fontSize:'12px',border:'1px dashed var(--b1)',borderRadius:'4px'}}>← Build leaderboard</div>
          }
          <LogPanel logs={logs} height={70}/>
        </div>
      )}
    </div>
  );
}
